import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import { CollectionsShell } from './CollectionsShell';
import type { ArTabId } from './CollectionsShell';
import { SetSwitch } from '../SettingsShell/controls';
import {
  AR_NUMBERS,
  arFollowUpStore,
  arFollowUpsHeader,
  deleteArFollowUp,
  duplicateArFollowUp,
  reorderArFollowUps,
  setArFollowUpState,
} from '../../ar/mockData';
import type { ArFollowUp } from '../../ar/types';

export interface ArFollowUpsViewProps {
  onSelectTab?: (tab: ArTabId) => void;
  onOpenSettings?: () => void;
  onCreateLink?: () => void;
  onNewFollowUp?: () => void;
  onReport?: (id: string) => void;
  onEdit?: (id: string) => void;
  onContinue?: (id: string) => void;
}

/**
 * Collections › Follow-ups (amendment 28 §2.2, board `AR2FollowUps`).
 *
 * The `/recall` list, as TC's Campaigns (spec 27 §4): drag to reorder, the Active
 * switch, Report/Edit/Duplicate/Delete on a live follow-up and Continue on a
 * draft. A patient is in one follow-up at a time — the one higher on the list.
 */
export function ArFollowUpsView({ onSelectTab, onOpenSettings, onCreateLink, onNewFollowUp, onReport, onEdit, onContinue }: ArFollowUpsViewProps) {
  const [, force] = useState(0);
  const rerender = () => { force((n) => n + 1); };
  const rows = arFollowUpStore.followUps;

  return (
    <CollectionsShell
      active="follow-ups"
      eyebrow={arFollowUpsHeader()}
      onSelectTab={onSelectTab}
      onOpenSettings={onOpenSettings}
      onCreateLink={onCreateLink}
      onNewFollowUp={onNewFollowUp}
    >
      {/* tiles */}
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="In a follow-up" value={String(AR_NUMBERS.inFollowUp)} sub={`of ${String(AR_NUMBERS.followablePatients)} who can be followed up`} />
        <Tile label="Collected · last 30 days" value={AR_NUMBERS.collected30} sub={`from ${String(AR_NUMBERS.collectedPatients)} patients`} />
        <Tile label="Through the payment link" value={`${String(AR_NUMBERS.throughLinkPct)}%`} sub={`${AR_NUMBERS.throughLink} · the rest at the desk or by phone`} />
        <Tile label="Median days to pay" value={String(AR_NUMBERS.medianDaysToPay)} sub="from the first message" />
      </div>

      {/* the list */}
      <div className="mt-4 overflow-hidden rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className="grid grid-cols-[28px_1.9fr_0.8fr_0.55fr_1fr_0.95fr_1.4fr] gap-x-3 border-b border-[var(--border-default)] px-4 py-2.5 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
          <span /><span>Follow-up</span><span>State</span><span className="text-right">In it now</span><span>Steps</span><span>Last 30 days</span><span className="text-right">Actions</span>
        </div>
        {rows.map((f, i) => (
          <FollowUpRow
            key={f.id}
            f={f}
            onToggle={(on) => { setArFollowUpState(f.id, on ? 'active' : 'draft'); rerender(); }}
            onMove={(dir) => { const to = i + dir; if (to >= 0 && to < rows.length) { reorderArFollowUps(i, to); rerender(); } }}
            onReport={() => { onReport?.(f.id); }}
            onEdit={() => { onEdit?.(f.id); }}
            onContinue={() => { onContinue?.(f.id); }}
            onDuplicate={() => { duplicateArFollowUp(f.id); rerender(); }}
            onDelete={() => { deleteArFollowUp(f.id); rerender(); }}
          />
        ))}
      </div>

      <p className="m-0 mt-3 text-[12px] leading-[1.6] text-[var(--text-muted)]">
        <b className="font-semibold text-[var(--text-secondary)]">A patient is in one follow-up at a time</b> — the one higher in this list. Drag to change the order.{' '}
        {AR_NUMBERS.waiting} patients are waiting and join when they can be contacted. Over $1,000, a person makes any call instead of ELVA.
      </p>
    </CollectionsShell>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] p-3.5 shadow-[var(--shadow-card)]">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
      <div className="mt-1 font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.03em] text-[var(--text-primary)]">{value}</div>
      <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">{sub}</div>
    </div>
  );
}

function FollowUpRow({
  f, onToggle, onMove, onReport, onEdit, onContinue, onDuplicate, onDelete,
}: {
  f: ArFollowUp;
  onToggle: (on: boolean) => void;
  onMove: (dir: -1 | 1) => void;
  onReport: () => void;
  onEdit: () => void;
  onContinue: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const draft = f.state === 'draft';
  return (
    <div className="grid grid-cols-[28px_1.9fr_0.8fr_0.55fr_1fr_0.95fr_1.4fr] items-center gap-x-3 border-b border-[var(--border-default)] px-4 py-3 text-[12.5px] last:border-0">
      <span className="flex flex-col text-[var(--text-muted)]">
        <button type="button" aria-label="Move up" onClick={() => { onMove(-1); }} className="hover:text-[var(--brand-indigo)]"><Icon kind="up" size={11} stroke={2.2} /></button>
        <button type="button" aria-label="Move down" onClick={() => { onMove(1); }} className="hover:text-[var(--brand-indigo)]"><Icon kind="down" size={11} stroke={2.2} /></button>
      </span>
      <span className="min-w-0">
        <span className="block font-semibold text-[var(--text-primary)]">{f.name}</span>
        <span className="block truncate text-[11px] text-[var(--text-muted)]">{f.description}</span>
      </span>
      <span>
        {draft ? (
          <span className="rounded-[5px] bg-[#F1F3F9] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">Draft</span>
        ) : (
          <SetSwitch label={`${f.name} active`} checked={f.state === 'active'} onChange={onToggle} />
        )}
      </span>
      <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{draft ? '—' : f.inProgress}</span>
      <span className="text-[11.5px] text-[var(--text-secondary)]">
        {draft ? 'No steps yet' : `${String(f.stepCount)} step${f.stepCount === 1 ? '' : 's'}`}
        {draft ? null : <span className="block text-[10.5px] text-[var(--text-muted)]">{f.channels}</span>}
      </span>
      <span className="text-[11.5px]">
        {draft ? '—' : (
          <>
            <span className="font-semibold text-[var(--text-primary)]">{f.paid30} paid</span>
            <span className="block font-[var(--font-mono)] text-[10.5px] text-[var(--money-green)]">{f.value30}</span>
          </>
        )}
      </span>
      <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-[11.5px] font-semibold text-[var(--brand-indigo)]">
        {draft ? (
          <button type="button" onClick={onContinue} className="hover:underline">Continue</button>
        ) : (
          <>
            <button type="button" onClick={onReport} className="hover:underline">Report</button>
            <button type="button" onClick={onEdit} className="hover:underline">Edit</button>
          </>
        )}
        <button type="button" onClick={onDuplicate} className="hover:underline">Duplicate</button>
        <button type="button" onClick={onDelete} className="text-[var(--alert-red)] hover:underline">Delete</button>
      </span>
    </div>
  );
}
